import { useState } from 'react'
import { BookOpen, ChevronDown, ChevronRight, Pencil, Plus, Sparkles } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLeaveTypeAdmin, useLeaveTypeMutations } from '../api'
import { useLegalEntities } from '@/features/org'
import { Select } from '@/components/ui/select'
import { LeaveTypeForm } from '../components/LeaveTypeForm'
import { LeaveTypeTierEditor } from '../components/LeaveTypeTierEditor'
import { toApiProblem } from '@/lib/api/client'
import type { LeaveTypeDetailView } from '../types'

/**
 * Leave types, per legal entity, with the article each statutory figure comes from.
 *
 * <p>Every seeded day count is data with its source on the row, not a constant somewhere in
 * the code. That is deliberate: a legal amendment, or a reading somebody disputes, should be
 * an edit on this screen rather than a release — and an entity that edits one keeps the edit
 * when the pack is re-applied.
 */
export function LeaveTypesPage() {
  useBreadcrumb()
  const types = useLeaveTypeAdmin()
  const { seedUae, deactivate } = useLeaveTypeMutations()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editing, setEditing] = useState<LeaveTypeDetailView | null>(null)
  const [adding, setAdding] = useState(false)
  const [retiring, setRetiring] = useState<LeaveTypeDetailView | null>(null)
  const [retireError, setRetireError] = useState<string | null>(null)
  const [seedEntity, setSeedEntity] = useState('')
  const entities = useLegalEntities()
  const [seedMessage, setSeedMessage] = useState<string | null>(null)
  const [seedError, setSeedError] = useState<string | null>(null)

  async function applyPack() {
    setSeedError(null)
    setSeedMessage(null)
    try {
      const result = await seedUae.mutateAsync(seedEntity)
      setSeedMessage(result.message)
    } catch (caught) {
      setSeedError(caught instanceof Error ? caught.message : 'The pack could not be applied')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave types"
        subtitle="What each kind of leave is worth, and where that figure comes from."
        actions={
          <PermissionGate permission="leave:configure">
            <Button type="button" onClick={() => { setEditing(null); setAdding(true) }}>
              <Plus className="size-4" aria-hidden="true" />
              New leave type
            </Button>
          </PermissionGate>
        }
      />

      <LeaveTypeForm
        key={editing?.id ?? 'new'}
        editing={editing}
        open={adding || editing !== null}
        onOpenChange={(open) => { if (!open) { setAdding(false); setEditing(null) } }}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this type'}?`}
        description="It stops being offered on new requests. Every request, balance and payslip that already names it keeps resolving — this is never a delete."
        confirmLabel="Retire it"
        loading={deactivate.isPending}
        error={retireError}
        onConfirm={() => {
          if (!retiring) return
          setRetireError(null)
          deactivate.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setRetireError(
              toApiProblem(caught)?.detail ?? 'That type could not be retired.'),
          })
        }}
      />

      <Card className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <Sparkles className="size-4 text-[var(--color-primary)]" aria-hidden="true" />
          UAE statutory pack
        </h2>
        <p className="text-sm text-[var(--color-muted)]">
          Creates the entitlements under Federal Decree-Law 33/2021 for one entity — annual,
          sick with its three pay bands, maternity, parental, bereavement, study and unpaid.
          It only adds what is missing and never overwrites an entity's own edits, so running
          it twice changes nothing.
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            void applyPack()
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="seed-entity" required>
              Legal entity
            </Label>
            {/* A picker, not a pasted id: the API takes the entity's UUID. */}
            <Select
              id="seed-entity"
              required
              value={seedEntity}
              onChange={(event) => setSeedEntity(event.target.value)}
              className="w-80"
            >
              <option value="" disabled>
                {entities.isLoading ? 'Loading entities…' : 'Select a legal entity'}
              </option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
          </div>
          <Button type="submit" variant="secondary" disabled={seedUae.isPending}>
            {seedUae.isPending ? 'Applying…' : 'Apply the pack'}
          </Button>
        </form>
        {seedMessage && (
          <p role="status" className="text-sm text-[var(--color-success)]">
            {seedMessage}
          </p>
        )}
        {seedError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {seedError}
          </p>
        )}
      </Card>

      <Card>
        {types.isPending && <Skeleton className="m-4 h-40" />}
        {types.isError && <ErrorState error={types.error} onRetry={() => void types.refetch()} />}
        {types.isSuccess && types.data.length === 0 && (
          <EmptyState
            title="No leave types yet"
            description="Apply the statutory pack above, or add your own."
          />
        )}
        {(types.data?.length ?? 0) > 0 && (
          <ul className="divide-y divide-[var(--color-border)]">
            {types.data?.map((type) => (
              <TypeRow
                key={type.id}
                type={type}
                open={expanded === type.id}
                onToggle={() => setExpanded(expanded === type.id ? null : type.id)}
                onEdit={() => { setAdding(false); setEditing(type) }}
                onRetire={() => { setRetireError(null); setRetiring(type) }}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function TypeRow({
  type,
  open,
  onToggle,
  onEdit,
  onRetire,
}: {
  type: LeaveTypeDetailView
  open: boolean
  onToggle: () => void
  onEdit: () => void
  onRetire: () => void
}) {
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-[var(--color-page)]"
      >
        <Chevron className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-[var(--color-heading)]">{type.name}</span>
            <code className="text-xs text-[var(--color-muted)]">{type.code}</code>
            {!type.active && <StatusBadge status="INACTIVE" tone="neutral" />}
            {type.statutoryCode ? (
              <StatusBadge status="STATUTORY" tone="info" label="Statutory" />
            ) : (
              <StatusBadge status="POLICY" tone="neutral" label="Company policy" />
            )}
          </span>
          <span className="mt-0.5 block text-sm text-[var(--color-muted)]">
            {type.annualEntitlementDays
              ? `${type.annualEntitlementDays} ${type.dayUnit === 'CALENDAR_DAYS' ? 'calendar' : 'working'} days`
              : type.maxDaysPerYear
                ? `up to ${type.maxDaysPerYear} days a year`
                : 'no day count set'}
            {type.paid ? ' · paid' : ' · unpaid'}
            {type.requiresRejoinConfirmation && ' · return confirmed'}
            {type.airTicketEligible && ' · uses the air-ticket entitlement'}
          </span>
        </span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-[var(--color-border)] bg-[var(--color-page)] p-4">
          <PermissionGate permission="leave:configure">
            <div className="flex gap-2">
              <Button
                type="button" size="sm" variant="outline"
                aria-label={`Edit ${type.name}`}
                onClick={onEdit}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </Button>
              {type.active && (
                <Button type="button" size="sm" variant="outline" onClick={onRetire}>
                  Retire
                </Button>
              )}
            </div>
          </PermissionGate>

          {type.statutoryReference && (
            <p className="flex items-start gap-2 text-sm text-[var(--color-body)]">
              <BookOpen className="mt-0.5 size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
              <span>{type.statutoryReference}</span>
            </p>
          )}

          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Detail label="Counted in" value={type.dayUnit === 'CALENDAR_DAYS' ? 'Calendar days' : 'Working days'} />
            <Detail label="Accrual" value={type.accrualBasis.replace('_', ' ').toLowerCase()} />
            <Detail
              label="Service before any entitlement"
              value={type.minServiceMonths == null ? 'None' : `${type.minServiceMonths} months`}
            />
            <Detail
              label="Full entitlement after"
              value={
                type.fullEntitlementAfterMonths == null
                  ? '—'
                  : `${type.fullEntitlementAfterMonths} months (${type.partialAccrualDaysPerMonth ?? '—'} days/month before that)`
              }
            />
            <Detail
              label="Carry forward"
              value={type.carryForwardMaxDays == null ? 'Not allowed' : String(type.carryForwardMaxDays)}
            />
            <Detail label="Encashable" value={type.encashable ? 'Yes' : 'No'} />
          </dl>

          <section className="space-y-2">
            <h3 className="text-sm font-medium text-[var(--color-heading)]">Pay bands</h3>
            {type.tiers.length === 0 ? (
              <p className="text-sm text-[var(--color-muted)]">
                One rate throughout — {type.paid ? 'paid in full' : 'unpaid'}. Add bands if this
                entitlement steps down.
              </p>
            ) : null}
            <LeaveTypeTierEditor type={type} />
          </section>
        </div>
      )}
    </li>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[var(--color-border)] py-1 last:border-0">
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="text-right text-[var(--color-heading)]">{value}</dd>
    </div>
  )
}
